import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { LegalLayout, InfoBlock, type LegalSection } from "@/components/calv/LegalLayout";

const sections: LegalSection[] = [
  {
    id: "ueberblick",
    num: "01",
    title: "Überblick",
    content: (
      <>
        <p>
          Der Schutz Ihrer persönlichen Daten ist uns ein wichtiges Anliegen. Wir behandeln Ihre
          personenbezogenen Daten vertraulich und entsprechend den gesetzlichen
          Datenschutzvorschriften sowie dieser Datenschutzerklärung.
        </p>
        <p>
          Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren
          personenbezogenen Daten passiert, wenn Sie diese Website besuchen.
        </p>
      </>
    ),
  },
  {
    id: "verantwortlich",
    num: "02",
    title: "Verantwortliche Stelle",
    content: (
      <>
        <p>Verantwortlich für die Datenverarbeitung auf dieser Website ist:</p>
        <InfoBlock>
          calvarius GmbH<br />
          Kalvarienberg 20<br />
          97753 Karlstadt<br />
          Deutschland<br />
          Telefon: +49 9353 9768 000<br />
          E-Mail: <a href="mailto:info@calvarius.com">info@calvarius.com</a>
        </InfoBlock>
      </>
    ),
  },
  {
    id: "allgemein",
    num: "03",
    title: "Allgemeine Hinweise",
    content: (
      <p>
        Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden
        können. Ausführliche Informationen zum Thema Datenschutz entnehmen Sie der nachfolgenden
        Datenschutzerklärung.
      </p>
    ),
  },
  {
    id: "logfiles",
    num: "04",
    title: "Server-Log-Dateien",
    content: (
      <>
        <p>
          Der Provider der Seiten erhebt und speichert automatisch Informationen in sogenannten
          Server-Log-Dateien, die Ihr Browser automatisch an uns übermittelt. Dies sind
          insbesondere:
        </p>
        <ul>
          <li>Browsertyp und Browserversion</li>
          <li>verwendetes Betriebssystem</li>
          <li>Referrer URL</li>
          <li>Hostname des zugreifenden Rechners</li>
          <li>Uhrzeit der Serveranfrage</li>
          <li>IP-Adresse</li>
        </ul>
        <p>Eine Zusammenführung dieser Daten mit anderen Datenquellen wird nicht vorgenommen.</p>
      </>
    ),
  },
  {
    id: "kontakt",
    num: "05",
    title: "Kontaktaufnahme",
    content: (
      <>
        <p>
          Wenn Sie uns per Kontaktformular, E-Mail oder Telefon Anfragen zukommen lassen, werden
          Ihre Angaben inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung
          der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert.
        </p>
        <p>Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.</p>
      </>
    ),
  },
  {
    id: "analyse",
    num: "06",
    title: "Analyse-Tools und Tracking",
    content: (
      <>
        <p>
          Zum Zweck der bedarfsgerechten Gestaltung und fortlaufenden Optimierung unserer Seiten
          nutzen wir Analyse- und Tracking-Tools. Aktuell setzen wir hierfür{" "}
          <strong>PostHog</strong> (Anbieter: PostHog Inc., 2261 Market Street #4008, San
          Francisco, CA 94114, USA; Hosting der Daten in der EU) ein. Dabei können
          personenbezogene Daten verarbeitet werden, insbesondere:
        </p>
        <ul>
          <li>Nutzungsdaten</li>
          <li>Geräteinformationen</li>
          <li>Interaktionsdaten</li>
        </ul>
        <p>
          Die Verarbeitung erfolgt auf Grundlage Ihrer Einwilligung gemäß Art. 6 Abs. 1 lit. a
          DSGVO.
        </p>

        <h3 id="posthog" className="scroll-mt-24">
          Webanalyse mit PostHog
        </h3>
        <p>
          <strong>Anbieter:</strong> PostHog Inc., 2261 Market Street #4008, San Francisco, CA
          94114, USA. Hosting unserer Daten erfolgt ausschließlich auf Servern in der
          Europäischen Union (Frankfurt, AWS).
        </p>
        <p>
          <strong>Zweck:</strong> Wir nutzen PostHog, um zu verstehen, wie Besucher unsere
          Website nutzen, welche Inhalte besonders gefragt sind und wie wir das Angebot
          verbessern können. Erfasst werden u.a. aufgerufene Seiten, Klicks auf Schaltflächen,
          Verweildauer, ungefähre geografische Region (Stadt-/Regionsebene), Browser-Typ,
          Betriebssystem, Bildschirmgröße und Referrer.
        </p>
        <p>
          <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung). Die
          Verarbeitung erfolgt ausschließlich, wenn Sie über unseren Cookie-Banner in die
          Kategorie „Statistik / Analyse" eingewilligt haben.
        </p>
        <p>
          <strong>Speicherort:</strong> Alle Daten werden in der EU (Frankfurt, AWS) verarbeitet
          und gespeichert. PostHog Inc. hat Zugriff zu Wartungs- und Supportzwecken; hierfür
          liegt ein Auftragsverarbeitungsvertrag nach Art. 28 DSGVO vor.
        </p>
        <p>
          <strong>Speicherdauer:</strong> 12 Monate, danach automatische Löschung oder
          Aggregation.
        </p>
        <p>
          <strong>Session Replays:</strong> Bildschirmaufzeichnungen werden erstellt.
          Eingabefelder (Passwörter, E-Mail, Formularinhalte) sind grundsätzlich maskiert und
          werden nicht aufgezeichnet.
        </p>
        <p>
          <strong>Widerruf:</strong> Jederzeit über die Cookie-Einstellungen widerrufbar (Link
          im Footer).
        </p>
        <p>
          <strong>Datenschutzhinweise des Anbieters:</strong>{" "}
          <a href="https://posthog.com/privacy">https://posthog.com/privacy</a>
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    num: "07",
    title: "Cookies",
    content: (
      <>
        <p>
          Unsere Website verwendet Cookies. Cookies sind kleine Textdateien, die auf Ihrem
          Endgerät gespeichert werden und die Ihr Browser speichert.
        </p>
        <p>Sie dienen dazu, unser Angebot nutzerfreundlicher, effektiver und sicherer zu machen.</p>
      </>
    ),
  },
  {
    id: "rechte",
    num: "08",
    title: "Ihre Rechte",
    content: (
      <>
        <p>Sie haben jederzeit das Recht auf:</p>
        <ul>
          <li>Auskunft über Ihre gespeicherten personenbezogenen Daten</li>
          <li>Berichtigung oder Löschung dieser Daten</li>
          <li>Einschränkung der Verarbeitung</li>
          <li>Widerspruch gegen die Verarbeitung</li>
          <li>Datenübertragbarkeit</li>
        </ul>
        <p>
          Hierzu sowie zu weiteren Fragen zum Thema Datenschutz können Sie sich jederzeit an uns
          wenden.
        </p>
      </>
    ),
  },
  {
    id: "widerruf",
    num: "09",
    title: "Widerruf Ihrer Einwilligung",
    content: (
      <p>
        Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen Einwilligung möglich.
        Sie können eine bereits erteilte Einwilligung jederzeit widerrufen.
      </p>
    ),
  },
  {
    id: "ssl",
    num: "10",
    title: "SSL- bzw. TLS-Verschlüsselung",
    content: (
      <p>
        Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher
        Inhalte eine SSL- bzw. TLS-Verschlüsselung.
      </p>
    ),
  },
  {
    id: "aenderungen",
    num: "11",
    title: "Änderung dieser Datenschutzerklärung",
    content: (
      <p>
        Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie stets den
        aktuellen rechtlichen Anforderungen entspricht oder um Änderungen unserer Leistungen
        umzusetzen.
      </p>
    ),
  },
  {
    id: "lighthouse-tool",
    num: "12",
    title: "Lighthouse-Live-Check-Tool",
    content: (
      <>
        <p>
          Unser Lighthouse-Live-Check unter <code>/tools/lighthouse-check</code> verarbeitet
          folgende Daten:
        </p>
        <ul>
          <li>Die von Ihnen eingegebene Website-URL</li>
          <li>
            Die von Google PageSpeed Insights ermittelten Audit-Ergebnisse
            (Lighthouse-Scores und Core Web Vitals)
          </li>
          <li>Den Zeitstempel der Audit-Durchführung</li>
        </ul>
        <p>
          Diese Daten werden in unserer Datenbank gespeichert und zur internen Analyse der
          Tool-Nutzung verwendet. Wir erheben <strong>keine</strong> IP-Adressen,{" "}
          <strong>keine</strong> User-Agent-Strings, <strong>keine</strong> Browser-Fingerprints
          und <strong>keine</strong> weiteren personenbezogenen Daten.
        </p>
        <p>
          Die geprüfte URL wird zusätzlich an die Google PageSpeed Insights API übermittelt,
          deren Datenschutz-Regelung Sie unter{" "}
          <a href="https://policies.google.com/privacy">policies.google.com/privacy</a>{" "}
          einsehen können.
        </p>
        <p>
          Rechtsgrundlage: berechtigtes Interesse (Art. 6 Abs. 1 lit. f DSGVO) zur Verbesserung
          unseres Angebots und Identifikation qualifizierter Anfrage-Interessenten.
        </p>
      </>
    ),
  },
];

export const Route = createFileRoute("/datenschutzerklaerung")({
  head: () => {
    const h = pageHead("privacy", "de");
    return { meta: h.meta, links: h.links };
  },
  component: () => (
    <LegalLayout
      eyebrow="DS."
      title="Datenschutzerklärung"
      meta="Gemäß DSGVO"
      crumbLabel="Datenschutz"
      sections={sections}
    />
  ),
});
